import { useContext, useEffect, useRef, useState } from 'react';
import axios from "axios";
import { navigate, useNavigationPrompt, useQueryParams } from 'raviger';
import { Formik } from 'formik';
import { Form as BootstrapForm, Button, ButtonGroup, Card, Modal, Row } from "react-bootstrap";
import * as Yup from 'yup';
import { Typeahead } from 'react-bootstrap-typeahead';
import { AddressSearch, ToggleSwitch, TextInput } from '../components/Form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft } from '@fortawesome/free-solid-svg-icons';
import ButtonSpinner from '../components/ButtonSpinner';
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';

// Form for owner and reporter Person objects.
const PreRegOwnerForm = ({id, organization}) => {

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  // Regex validators.
  const phoneRegex = /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,3}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/;
  const nameRegex = /^[a-z ,.'-]+$/i;
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  const initial_coordinates = [0, 0];

  const initialData = {
    id: null,
    first_name: '',
    last_name: '',
    phone: '',
    alt_phone: '',
    email: '',
    comments: '',
    drivers_license: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zip_code: '',
    evac_zone: '',
    latitude: null,
    longitude: null,
    training: false,
    change_reason: '',
    organization: state.organization.id,
  }

  // Initial Person data.
  const [data, setData] = useState(initialData);

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();
    if (id) {
      const fetchPersonData = async () => {
        // Fetch Person data.
        await axios.get('/people/api/pre-reg-person/' + id + '/', {
          cancelToken: source.token,
        })
        .then(response => {
          if (!unmounted) {
            // Set phone field to be the pretty version.
            response.data['phone'] = response.data['display_phone']
            response.data['alt_phone'] = response.data['display_alt_phone']
            // Initialize change_reason on fetch to avoid warning.
            response.data['change_reason'] = '';
            response.data['has_id'] = id ? true : false;
            setData(response.data);
          }
        })
        .catch(error => {
          if (!unmounted) {
            setShowSystemError(true);
          }
        });
      };
      fetchPersonData();
    }

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id]);

  return (
    <>
      <Formik
        initialValues={data}
        enableReinitialize={true}
        validationSchema={Yup.object({
          has_id: Yup.boolean(),
          first_name: Yup.string()
            .max(50, 'Must be 50 characters or less')
            .matches(nameRegex, "Name is not valid")
            .required('Required'),
          last_name: Yup.string()
            .max(50, 'Must be 50 characters or less')
            .matches(nameRegex, "Name is not valid")
            .required('Required'),
          phone: Yup.string()
            .matches(phoneRegex, "Phone number is not valid")
            .min(10, "Phone number is not valid")
            .required('Required'),
          alt_phone: Yup.string()
            .matches(phoneRegex, "Phone number is not valid"),
          email: Yup.string()
            .max(200, 'Must be 200 characters or less')
            .matches(emailRegex, "Email is not valid"),
          comments: Yup.string(),
          drivers_license: Yup.string(),
          address: Yup.string().required('Required'),
          apartment: Yup.string()
            .max(10, 'Must be 10 characters or less'),
          city: Yup.string().required('Required'),
          state: Yup.string().required('Required'),
          zip_code: Yup.string(),
          evac_zone: Yup.string()
            .max(100, 'Must be 100 characters or less'),
          latitude: Yup.number()
            .nullable(),
          longitude: Yup.number()
            .nullable(),
          change_reason: Yup.string()
            .when('has_id', {
              is: true,
              then: Yup.string().required('Required').max(50, 'Must be 50 characters or less')}),
        })}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          if (id) {
            axios.put('/people/api/pre-reg-person/' + id + '/', values)
            .then(response => {
              navigate('/' + organization + '/manage/owners/' + response.data.id);
            })
            .catch(error => {
              setShowSystemError(true);
              setSubmitting(false);
            });
          }
          else {
            axios.post('/people/api/pre-reg-person/', values)
            .then(response => {
              navigate('/' + organization + '/manage/owners/' + response.data.id);
            })
            .catch(error => {
              setShowSystemError(true);
              setSubmitting(false);
            });
          }
        }}
      >
        {formikProps => (
          <Card border="secondary" className="mt-5">
            <Card.Header as="h5" className="ps-3">
              <span id="back" style={{cursor:'pointer'}} onClick={(e) => {id ? navigate("/" + organization + "/manage/owners/" + id) : navigate("/" + organization + "/manage/owners")}} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>
              Preregistered Owner Information
            </Card.Header>
          <Card.Body>
          <BootstrapForm noValidate>
            <Row className="gx-2">
              <TextInput
                xs="6"
                type="text"
                label="First Name*"
                name="first_name"
              />
              <TextInput
                xs="6"
                type="text"
                label="Last Name*"
                name="last_name"
              />
            </Row>
            <Row className="mt-3 gx-2">
              <TextInput
                xs="2"
                type="text"
                label="Phone*"
                name="phone"
              />
              <TextInput
                xs="2"
                type="text"
                label="Alternate Phone"
                name="alt_phone"
              />
              <TextInput
                xs="2"
                type="text"
                label="Drivers License"
                name="drivers_license"
                id="drivers_license"
              />
              <TextInput
                xs="6"
                type="text"
                label="Evacuation Zone"
                name="evac_zone"
                id="evac_zone"
              />
            </Row>
            <Row className="mt-3">
              <TextInput
                xs="12"
                type="text"
                label="Email"
                name="email"
              />
            </Row>
            <Row className="mt-3">
              <TextInput
                xs="12"
                as="textarea"
                label="Comments / Alternate Contact"
                name="comments"
              />
            </Row>
            <AddressSearch formikProps={formikProps} label={"Search for Owner Address"} show_apt={true} show_same={false} always_show={true} address_form={true} initial_coordinates={initial_coordinates} isOwner={true} is_intake={false} is_required={true} />
            <Row className="mt-3">
              <ToggleSwitch xs={4} name="training" id="training" label="Training" />
            </Row>
            {id ? <Row className="mt-2">
              <TextInput
                xs="12"
                type="text"
                label={"Change Reason*"}
                name="change_reason"
              />
            </Row> : ""}
          </BootstrapForm>
          </Card.Body>
            <ButtonGroup size="lg" >
              {/* form save buttons */}
                <ButtonSpinner isSubmitting={formikProps.isSubmitting} isSubmittingText="Saving..." type="button" onClick={() => { formikProps.submitForm() }}>
                  <span>{!id ? "Add Preregistered Owner" : "Save"}</span>
                </ButtonSpinner>
            </ButtonGroup>
          </Card>
        )}
      </Formik>
    </>
  );
};

export default PreRegOwnerForm;
